import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import { getLiveBoardChargers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/common/StatusBadge';
import { ConnectorBadges } from '../components/common/ConnectorBadges';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Zap, WifiOff, AlertTriangle, Battery, Clock, MapPin, ArrowLeft } from 'lucide-react';

const POLL_INTERVAL_MS = 5000;

const BUSY_STATUSES = new Set([
  'Charging',
  'Occupied',
  'Preparing',
  'Finishing',
  'SuspendedEV',
  'SuspendedEVSE',
]);

const isChargerFree = (charger) => {
  if (!charger.is_online) return false;
  const connectors = charger.connectors || [];
  if (connectors.length === 0) return charger.status === 'Available';
  return connectors.every((c) => c.status === 'Available');
};

const isChargerBusy = (charger) => {
  if (!charger.is_online) return false;
  const connectors = charger.connectors || [];
  if (connectors.length === 0) return BUSY_STATUSES.has(charger.status);
  return connectors.some((c) => BUSY_STATUSES.has(c.status));
};

const formatClock = (date) =>
  date.toLocaleTimeString('sq-AL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const formatRemaining = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return null;
  const m = Math.max(0, Math.round(Number(minutes)));
  if (m < 60) return `~${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `~${h}h ${rem}min` : `~${h}h`;
};

const getConnectorLabel = (connectorId) => {
  const n = Number(connectorId) || 1;
  return String.fromCharCode(64 + n);
};

const getChargingConnectors = (charger) =>
  (charger.connectors || []).filter(
    (c) =>
      BUSY_STATUSES.has(c.status) ||
      c.soc_percent != null ||
      (c.current_power_kw || 0) > 0
  );

const getPrimarySoc = (charger) => {
  const charging = getChargingConnectors(charger);
  for (const c of charging) {
    if (c.soc_percent != null) return c.soc_percent;
  }
  return null;
};

const getDisplayStatus = (charger) => {
  if (!charger.is_online) return charger.status === 'Reconnecting' ? 'Reconnecting' : 'Offline';
  const connectors = charger.connectors || [];
  if (connectors.length === 0) return charger.status || 'Available';

  const statuses = connectors.map((c) => c.status);
  if (statuses.some((s) => s === 'Faulted')) return 'Faulted';
  if (statuses.some((s) => s === 'Charging')) return 'Charging';
  if (statuses.some((s) => ['Occupied', 'Finishing', 'SuspendedEV', 'SuspendedEVSE'].includes(s))) {
    return 'Occupied';
  }
  if (statuses.some((s) => s === 'Preparing')) return 'Preparing';
  if (statuses.every((s) => s === 'Available')) return 'Available';
  if (BUSY_STATUSES.has(charger.status)) return charger.status;
  return charger.status || 'Available';
};

const groupByStation = (chargers) => {
  const map = new Map();
  for (const charger of chargers) {
    const id = charger.site_id || '_none';
    if (!map.has(id)) {
      map.set(id, {
        siteId: charger.site_id || null,
        siteName: charger.site_name || 'Pa stacion',
        city: charger.city || '',
        chargers: [],
      });
    }
    map.get(id).chargers.push(charger);
  }
  return Array.from(map.values()).sort((a, b) =>
    a.siteName.localeCompare(b.siteName, 'sq')
  );
};

const ChargingInfoCell = ({ charger }) => {
  const busy = (charger.connectors || []).filter((c) => {
    if (!charger.is_online && c.status !== 'Charging') return false;
    return (
      BUSY_STATUSES.has(c.status) ||
      c.soc_percent != null ||
      (c.current_power_kw || 0) > 0 ||
      (c.energy_kwh || 0) > 0 ||
      c.remaining_minutes != null
    );
  });

  if (busy.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      {busy.map((conn) => {
        const label = getConnectorLabel(conn.connector_id);
        const remaining = formatRemaining(conn.remaining_minutes);
        const power = conn.current_power_kw || 0;
        const energy = conn.energy_kwh || 0;
        const elapsed =
          conn.elapsed_minutes != null
            ? formatRemaining(conn.elapsed_minutes)?.replace('~', '')
            : null;
        return (
          <div
            key={conn.id || conn.connector_id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm leading-tight"
          >
            <span className="text-muted-foreground font-semibold">P{label}</span>
            {conn.soc_percent != null && (
              <span className="inline-flex items-center gap-1 text-emerald-500 font-semibold tabular-nums">
                <Battery className="w-3.5 h-3.5" />
                {conn.soc_percent}%
              </span>
            )}
            {power > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-500 tabular-nums">
                <Zap className="w-3.5 h-3.5" />
                {Number(power).toFixed(1)} kW
              </span>
            )}
            {energy > 0 && (
              <span className="text-foreground/80 tabular-nums font-medium">
                {Number(energy).toFixed(1)} kWh
              </span>
            )}
            {remaining && (
              <span className="inline-flex items-center gap-1 text-blue-500 tabular-nums">
                <Clock className="w-3.5 h-3.5" />
                {remaining} mbetur
              </span>
            )}
            {elapsed && !remaining && (
              <span className="inline-flex items-center gap-1 text-muted-foreground tabular-nums">
                <Clock className="w-3.5 h-3.5" />
                {elapsed}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ChargerRow = ({ charger, hideLocation }) => {
  const subtitle = [charger.vendor, charger.model].filter(Boolean).join(' · ');
  const location = [charger.site_name, charger.city].filter(Boolean).join(', ');
  const displayStatus = getDisplayStatus(charger);
  const primarySoc = getPrimarySoc(charger);

  return (
    <TableRow
      className="border-border hover:bg-muted/30"
      data-testid={`live-charger-row-${charger.charger_id}`}
    >
      <TableCell className="py-4 sm:py-5">
        <div className="flex items-center gap-3 sm:gap-4">
          <div
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
              displayStatus === 'Faulted'
                ? 'bg-red-500/10'
                : charger.is_online
                  ? 'bg-green-500/10'
                  : 'bg-muted'
            }`}
          >
            {displayStatus === 'Faulted' ? (
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
            ) : charger.is_online ? (
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
            ) : (
              <WifiOff className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-foreground text-base sm:text-lg truncate">
              {charger.name || charger.charger_id}
            </p>
            {subtitle && (
              <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
        </div>
      </TableCell>
      {!hideLocation && (
        <TableCell className="hidden md:table-cell py-4 sm:py-5">
          {location ? (
            <div className="min-w-0">
              <p className="text-base text-foreground truncate max-w-[220px]">
                {charger.site_name}
              </p>
              {charger.city && (
                <p className="text-sm text-muted-foreground truncate">{charger.city}</p>
              )}
            </div>
          ) : null}
        </TableCell>
      )}
      <TableCell className="py-4 sm:py-5">
        <div className="flex items-center gap-2">
          {charger.is_online ? (
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
          ) : (
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-500" />
          )}
          <StatusBadge
            status={displayStatus}
            isOnline={charger.is_online}
            showDot={false}
            socPercent={displayStatus === 'Charging' ? primarySoc : null}
          />
        </div>
      </TableCell>
      <TableCell className="py-4 sm:py-5">
        <ConnectorBadges
          connectors={charger.connectors || []}
          isChargerOnline={charger.is_online}
          maxPowerKw={charger.max_power_kw}
          chargerStatus={displayStatus}
          compact
        />
      </TableCell>
      <TableCell className="py-4 sm:py-5">
        <ChargingInfoCell charger={charger} />
      </TableCell>
    </TableRow>
  );
};

const StationSection = ({ station, hideLocation }) => {
  const free = station.chargers.filter(isChargerFree).length;
  const busy = station.chargers.filter(isChargerBusy).length;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 sm:px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
          {station.siteId ? (
            <Link
              to={`/live?site=${station.siteId}`}
              className="font-semibold text-base sm:text-lg text-foreground hover:text-primary truncate"
            >
              {station.siteName}
            </Link>
          ) : (
            <span className="font-semibold text-base sm:text-lg truncate">{station.siteName}</span>
          )}
          {station.city && (
            <span className="text-sm text-muted-foreground truncate hidden sm:inline">
              · {station.city}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
          <span>{station.chargers.length} karikues</span>
          <span className="text-green-500 font-medium">{free} të lirë</span>
          <span className="text-red-500 font-medium">{busy} të zënë</span>
          {station.siteId && (
            <Link
              to={`/live?site=${station.siteId}`}
              className="text-primary hover:underline font-medium"
            >
              Hap stacionin
            </Link>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-sm whitespace-nowrap py-3">
                Karikuesi
              </TableHead>
              {!hideLocation && (
                <TableHead className="text-muted-foreground text-sm whitespace-nowrap py-3 hidden md:table-cell">
                  Vendndodhja
                </TableHead>
              )}
              <TableHead className="text-muted-foreground text-sm whitespace-nowrap py-3">
                Statusi
              </TableHead>
              <TableHead className="text-muted-foreground text-sm whitespace-nowrap py-3">
                Portat
              </TableHead>
              <TableHead className="text-muted-foreground text-sm whitespace-nowrap py-3">
                Karikimi
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {station.chargers.map((charger) => (
              <ChargerRow
                key={charger.id || charger.charger_id}
                charger={charger}
                hideLocation={hideLocation}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const LiveStatusBoard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('site') || undefined;

  const [chargers, setChargers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [now, setNow] = useState(new Date());

  const role = String(user?.role || '').toUpperCase();
  const canAccess = role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'MODERATOR';

  const fetchData = useCallback(async () => {
    if (!canAccess) return;
    try {
      const res = await getLiveBoardChargers(siteId);
      setChargers(res.data?.chargers || []);
      setLastUpdated(res.data?.updated_at ? new Date(res.data.updated_at) : new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch live status:', err);
      const status = err?.response?.status;
      if (status === 401) {
        setError('Sesioni skadoi. Hyni përsëri.');
      } else if (status === 403) {
        setError('Nuk keni akses në këtë stacion.');
      } else {
        setError('Nuk u ngarkua statusi. Po provohet përsëri…');
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, canAccess]);

  useEffect(() => {
    if (!canAccess) return undefined;
    setLoading(true);
    fetchData();
    const pollId = setInterval(fetchData, POLL_INTERVAL_MS);
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(pollId);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchData, canAccess]);

  useEffect(() => {
    const clockId = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockId);
  }, []);

  const stations = useMemo(() => groupByStation(chargers), [chargers]);
  const activeStation = siteId ? stations[0] : null;
  const freeCount = chargers.filter(isChargerFree).length;
  const busyCount = chargers.filter(isChargerBusy).length;
  const offlineCount = chargers.filter((c) => !c.is_online).length;
  const title = activeStation
    ? activeStation.siteName
    : 'AlbMove Statusi i Karikuesve';

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground p-4 sm:p-6 lg:p-8"
      data-testid="live-status-board"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Zap className="w-7 h-7 text-primary" />
          </div>
          <div className="min-w-0">
            {siteId && (
              <Link
                to="/live"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Të gjitha stacionet
              </Link>
            )}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-outfit tracking-tight truncate">
              {title}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-0.5">
              {siteId
                ? 'Karikuesit e këtij stacioni · live'
                : 'Shfaqje live e porteve të lira dhe të zëna · ndarë sipas stacionit'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            <span className="text-sm font-medium text-green-500">Live</span>
          </div>
          <div className="text-xl sm:text-2xl font-mono font-semibold tabular-nums">
            {formatClock(now)}
          </div>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 max-w-2xl">
        <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3">
          <p className="text-xs sm:text-sm text-green-600 dark:text-green-400 font-medium">Të lirë</p>
          <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 tabular-nums">
            {freeCount}
          </p>
        </div>
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3">
          <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 font-medium">Të zënë</p>
          <p className="text-2xl sm:text-3xl font-bold text-red-600 dark:text-red-400 tabular-nums">
            {busyCount}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
          <p className="text-xs sm:text-sm text-muted-foreground font-medium">Offline</p>
          <p className="text-2xl sm:text-3xl font-bold text-muted-foreground tabular-nums">
            {offlineCount}
          </p>
        </div>
      </div>

      {error && (
        <p className="text-amber-500 text-sm mb-4" role="alert">
          {error}
        </p>
      )}

      {loading && chargers.length === 0 ? (
        <div className="rounded-xl border border-border bg-card flex flex-col items-center justify-center py-24">
          <div className="skeleton h-8 w-48 mb-4" />
          <div className="skeleton h-64 w-full max-w-3xl rounded-xl" />
        </div>
      ) : chargers.length === 0 ? (
        <div className="rounded-xl border border-border bg-card flex flex-col items-center justify-center py-24">
          <Zap className="w-14 h-14 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-lg">
            {siteId ? 'Nuk ka karikues për këtë stacion' : 'Nuk ka karikues aktivë'}
          </p>
          {siteId && (
            <Link to="/live" className="mt-3 text-primary hover:underline text-sm">
              Kthehu te të gjitha stacionet
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {stations.map((station) => (
            <StationSection
              key={station.siteId || station.siteName}
              station={station}
              hideLocation={Boolean(siteId)}
            />
          ))}
        </div>
      )}

      {lastUpdated && (
        <p className="text-xs sm:text-sm text-muted-foreground mt-4 text-center">
          Përditësuar: {formatClock(lastUpdated)} · Rifreskim çdo 5 sekonda
        </p>
      )}
    </div>
  );
};

export default LiveStatusBoard;
